import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import withHeaderFooter, { IWithHeaderFooterProps } from "@/app/csr-src/hocs/with-header-footer";
import styles from "./index.module.scss";
import { Avatar, Box } from "@mui/material";
import { useStores } from "@/app/csr-src/models";
import ArrowRight from "@/app/csr-src/assets/icons/ArrowRight.svg";
import Edit from "@/app/csr-src/assets/icons/review_edit.svg";
import Nut from "@/app/csr-src/assets/icons/editicon.svg";
import OrderIcon from "@/assets/icons/OrderIcon.svg";
import VoucherIcon from "@/assets/icons/VoucherIcon.svg";
import WishIcon from "@/assets/icons/WishIcon.svg";
import AddressIcon from "@/assets/icons/AddressIcon.svg";
import HistoryIcon from "@/assets/icons/HistoryIcon2.svg";
import InfoSetDialog from "@/app/csr-src/pages/profile/personalCenter/personalInfoSetDialog/index";
import AccountSetDialog from "@/app/csr-src/pages/profile/personalCenter/personalAccountSetDialog/index";
import type { UserAccountType } from "@/app/csr-src/apis/profile/profile.interface";
import { imageUtil } from "@/common-components-src/js/utils/imageUtil";
import GiftModal from "@/common-components-src/js/gift";
import Recommend from "@/common-components-src/js/recommend/index";
import { fetchUserCenterAccount, fetchGetOrderStatisInfo } from "@/app/csr-src/apis/profile/profile.api";
import { fetchGetActivityApi } from "@/app/csr-src/apis/invitePolitely/invitePolitely.api";
import { sendPv, sendClickTrack, EXPOSURE_RECORD } from "@/tracks/25118";
import { EXPOSURE_RECORD as user_EXPOSURE_RECORD, sendClickTrack as user_sendClickTrack } from "@/tracks/25354";

import _ from "lodash";
interface UserInfo {
  imageUrl?: string;
  nickName?: string;
}

interface Item {
  icon: React.ReactNode;
  name: string;
  num?: number;
  subTitle?: string;
  onClick: () => void;
}

type OrderInfoStaticType = {
  afterSaleCount: number;
  unpaidCount: number;
  waitCommentCount: number;
  waitRecCount: number;
};

const Profile = withHeaderFooter(
  observer(({ JDIHeader }: IWithHeaderFooterProps) => {
    const { t } = useTranslation();
    const [userInfo, setUserInfo] = useState<UserInfo | null>({});
    const [couponNum, setCouponNum] = useState<number>(0); // 未使用优惠券数
    const navigate = useNavigate();
    const [infoOpen, setInfoOpen] = useState(false);
    const [accountSetOpen, setAccountSetOpen] = useState(false);
    const { orderStore } = useStores();
    const [showActivity, setShowActivity] = useState(false);
    const [activityImage, setActivityImage] = useState("");
    const [activityUrl, setActivityUrl] = useState("");
    const [orderInfoStatic, setOrderInfoStatic] = useState<OrderInfoStaticType>();

    const itemsList: Item[] = [
      {
        icon: (
          <VoucherIcon
            exptag={"exp|" + EXPOSURE_RECORD.MyJD_Main_Coupons_Expo}
            data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
            className={styles.icon}
            width={24}
            height={24}
          />
        ),
        name: t("common.pageName.coupons"),
        subTitle: t(couponNum > 0 ? "profile.page.couponsSubTitle" : "profile.page.uncouponsSubTitle"),
        onClick: () => {
          sendClickTrack.MyJD_Main_Coupons();
          navigate("/profile/coupons-discounts");
        },
      },
      {
        icon: (
          <WishIcon
            exptag={"exp|" + EXPOSURE_RECORD.MyJD_Main_WishList_Expo}
            data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
            className={styles.icon}
            width={24}
            height={24}
          />
        ),
        name: t("common.pageName.wishlist"),
        subTitle: t("profile.page.wishListSubTitle"),
        onClick: () => {
          sendClickTrack.MyJD_Main_WishList();
          navigate("/profile/wishlist");
        },
      },
      {
        icon: (
          <HistoryIcon
            exptag={"exp|" + EXPOSURE_RECORD.MyJD_Main_History_Expo}
            data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
            className={styles.icon}
            width={24}
            height={24}
          />
        ),
        name: t("common.pageName.browsingHistory"),
        subTitle: t("profile.page.browsingHistorySubTitle"),
        onClick: () => {
          sendClickTrack.MyJD_Main_History();
          navigate("/profile/shopping-activity");
        },
      },
    ];

    // 点击个人编辑
    const handleClickEditProfile = () => {
      sendClickTrack.MyJD_Main_Name();
      setInfoOpen(true);
    };
    // 点击个人账户
    const handleClickAccount = () => {
      sendClickTrack.MyJD_Main_Settings();
      setAccountSetOpen(true);
    };
    // 点击订单
    const handleClickMyOrders = (key: string, num?: number) => {
      switch (key) {
        case "order":
          sendClickTrack.MyJD_Main_Orders();
          orderStore.setOrderType("4096");
          navigate("/profile/my-orders");
          break;
        case "unpaid":
          sendClickTrack.MyJD_Main_Orders_StatusBtn({
            icon_name: "unpaid",
          });
          orderStore.setOrderType("1");
          navigate("/profile/my-orders");
          break;
        case "pending":
          sendClickTrack.MyJD_Main_Orders_StatusBtn({
            icon_name: "pending",
          });
          orderStore.setOrderType("128");
          navigate("/profile/my-orders");
          break;
        case "review":
          sendClickTrack.MyJD_Main_Orders_StatusBtn({
            icon_name: "review",
          });
          navigate("/profile/my-reviews");
          break;
        case "returns":
          sendClickTrack.MyJD_Main_Orders_StatusBtn({
            icon_name: "return",
          });
          // 数量不为0，跳转至售后单列表页Processing tab下，否则跳转all
          if (num && num > 0) {
            navigate("/profile/my-returns?status=1");
          } else {
            navigate("/profile/my-returns?status=0");
          }
          // navigate("/OrderList");
          break;
      }
    };
    // 点击地址
    const handleClickAddress = () => {
      sendClickTrack.MyJD_Main_Addr();
      navigate("/profile/my-address");
    };
    // 关闭个人信息修改弹框
    const onlyClose = (v: string) => {
      if (v == "info") {
        setInfoOpen(false);
      } else if (v == "account") {
        setAccountSetOpen(false);
      }
    };

    const handleNumber = (num?: number) => {
      if (_.isNil(num)) {
        return "0";
      } else {
        return Number(num) > 99 ? "99+" : String(num);
      }
    };

    const getOrderStatisInfo = async () => {
      const res = await fetchGetOrderStatisInfo();
      if (res.code === "200") {
        setOrderInfoStatic(res.data);
      }
    };

    const handleInvitePolitely = () => {
      sendClickTrack.MyJD_Main_Invite();
      window.location.href = activityUrl;
    };
    const getInit = async () => {
      const res = await fetchUserCenterAccount([3, 6, 9]);
      console.log("sxt-getInit", res);
      if (res.code === "200") {
        const { accountList, userInfo } = res.data;
        setUserInfo(userInfo);
        accountList.length &&
          accountList.map((item: UserAccountType) => {
            switch (item.type) {
              case 3: // 优惠券数量
                setCouponNum(item.count > 0 ? item.count : 0);
                break;
            }
          });
      }
    };
    const getActivityStatisInfo = async () => {
      const res = await fetchGetActivityApi();
      if (res && res.code === "200") {
        const data = res?.data;
        const startTime = data?.startTime;
        const endTime = data?.endTime;
        const timestamp = Math.floor(Date.now());
        if (timestamp >= startTime && timestamp < endTime) {
          setShowActivity(true);
          setActivityImage(imageUtil.concatImgUrlWithoutForceHttps(data?.activityImage));
          setActivityUrl(data?.activityUrl);
        } else {
          setShowActivity(false);
        }
      }
    };

    useEffect(() => {
      getInit();
      getOrderStatisInfo();
      getActivityStatisInfo();

      sendPv.MyJD_Main();
    }, []);

    return (
      <div className={styles.profile_container}>
        <Helmet>
          <title>My profile | Joybuy</title>
          <meta
            name="description"
            content="Manage your account on Joybuy. View your order history, track shipments, and update personal details. Enjoy fast shipping and delivery guarantee on all your orders."
          />
        </Helmet>
        <GiftModal closeModalCallback={getInit} />
        <div className={styles.w}>
          <div className={styles.profile}>
            <div className={styles.me}>
              <div className={styles.profile_picture}>
                <Avatar
                  src={userInfo?.imageUrl ? imageUtil.concatImgUrlWithoutForceHttps(userInfo?.imageUrl, 60) : ""}
                  className={styles.picture}
                  alt=""
                  sx={{
                    bgcolor: "#E1E3E6",
                    "& .MuiSvgIcon-root": {
                      width: "100%",
                      height: "100%",
                      fill: "#F0F1F2",
                    },
                  }}
                />
                <div
                  className={styles.userName}
                  exptag={"exp|" + EXPOSURE_RECORD.MyJD_Main_Name_Expo}
                  data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                >
                  <div className={styles.nickName}>{userInfo?.nickName}</div>
                  <div className={styles.editBtns}>
                    <div className={styles.editBtn} onClick={() => handleClickEditProfile()}>
                      <Edit className={styles.iconEdit} width={24} height={24} />
                      {t("profile.page.editProfile")}
                    </div>
                    <div className={styles.divider}></div>
                    <div
                      exptag={"exp|" + EXPOSURE_RECORD.MyJD_Main_Settings_Expo}
                      data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                      className={styles.editBtn}
                      onClick={() => handleClickAccount()}
                    >
                      <Nut className={styles.iconNut} />
                      {t("profile.page.account")}
                    </div>
                    <div className={styles.divider}></div>
                    <div
                      exptag={"exp|" + EXPOSURE_RECORD.MyJD_Main_Addr_Expo}
                      data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                      className={styles.editBtn}
                      onClick={() => handleClickAddress()}
                    >
                      <AddressIcon className={styles.iconAddress} width={24} height={24} />
                      {t("profile.page.addresses")}
                    </div>
                  </div>
                </div>
              </div>
              {showActivity && (
                <div
                  exptag={"exp|" + EXPOSURE_RECORD.MyJD_Main_Invite_Expo}
                  data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                  className={styles.activityImg}
                  onClick={handleInvitePolitely}
                >
                  <img src={activityImage} alt="InvitePolitely" />
                </div>
              )}
            </div>

            <div className={styles.order}>
              <div
                exptag={"exp|" + EXPOSURE_RECORD.MyJD_Main_Orders_Expo}
                data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                className={styles.item}
                onClick={() => handleClickMyOrders("order")}
              >
                <OrderIcon className={styles.orderIcon} width={24} height={24} />
                <span className={styles.title}>
                  {t("common.pageName.myOrders")} <ArrowRight className={styles.arrowRight} width={16} height={16} />
                </span>
              </div>
              <div
                exptag={"exp|" + EXPOSURE_RECORD.MyJD_Main_Orders_StatusBtn_Expo}
                data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                data-exptag-json-param={JSON.stringify({ icon_name: "unpaid" })}
                className={styles.item}
                onClick={() => handleClickMyOrders("unpaid")}
              >
                <span className={styles.num}>{handleNumber(orderInfoStatic?.unpaidCount)}</span>
                <span className={styles.title}>{t("profile.page.unpaid")}</span>
              </div>
              <div
                exptag={"exp|" + EXPOSURE_RECORD.MyJD_Main_Orders_StatusBtn_Expo}
                data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                data-exptag-json-param={JSON.stringify({ icon_name: "pending" })}
                className={styles.item}
                onClick={() => handleClickMyOrders("pending")}
              >
                <span className={styles.num}>{handleNumber(orderInfoStatic?.waitRecCount)}</span>
                <span className={styles.title}>Processing</span>
              </div>
              <div
                exptag={"exp|" + EXPOSURE_RECORD.MyJD_Main_Orders_StatusBtn_Expo}
                data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                data-exptag-json-param={JSON.stringify({ icon_name: "review" })}
                className={styles.item}
                onClick={() => handleClickMyOrders("review")}
              >
                <span className={styles.num}>{handleNumber(orderInfoStatic?.waitCommentCount)}</span>
                <span className={styles.title}>{t("profile.page.review")}</span>
              </div>
              <div
                exptag={"exp|" + EXPOSURE_RECORD.MyJD_Main_Orders_StatusBtn_Expo}
                data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                data-exptag-json-param={JSON.stringify({ icon_name: "return" })}
                className={styles.item}
                onClick={() => handleClickMyOrders("returns", orderInfoStatic?.afterSaleCount)}
              >
                <span className={styles.num}>{handleNumber(orderInfoStatic?.afterSaleCount)}</span>
                <span className={styles.title}>{t("profile.page.returns")}</span>
              </div>
            </div>

            <div className={styles.moduleList}>
              {itemsList.map((item: Item) => {
                return (
                  <div className={styles.item} key={item.name} onClick={() => item.onClick()}>
                    <div className={styles.title}>
                      <span className={styles.names}>
                        {item.icon}
                        <span className={styles.name}>{item.name}</span>
                        {item.name === t("common.pageName.coupons") && couponNum > 0 && <span className={styles.num}>{couponNum}</span>}
                      </span>
                      <ArrowRight className={styles.arrowRight} width={16} height={16} />
                    </div>
                    <div className={styles.subTitle} dangerouslySetInnerHTML={{ __html: item.subTitle as string }}></div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <InfoSetDialog
          open={infoOpen}
          onlyClose={() => onlyClose("info")}
          onSaver={() => {
            getInit();
            setInfoOpen(false);
          }}
        />
        <AccountSetDialog open={accountSetOpen} onlyClose={() => onlyClose("account")} />
        <div className={styles.Recommend}>
          <Recommend
            sceneCode="user_center_page"
            trackParams={{
              RecomSku_Expo: user_EXPOSURE_RECORD.MyJD_Detail_RecomSku_Expo,
              RecomSku_Tab_Expo: user_EXPOSURE_RECORD.MyJD_Main_Detail_RecomSku_Tab_Expo,
              RecomSku_Addcart_Expo: user_EXPOSURE_RECORD.MyJD_Main_Detail_RecomSku_Addcart_Expo,
              productClick: user_sendClickTrack.MyJD_Main_Detail_RecomSku,
              tabClick: user_sendClickTrack.MyJD_Main_Detail_RecomSku_Tab,
              addCartClick: user_sendClickTrack.MyJD_Main_Detail_RecomSku_Addcart,
            }}
          />
        </div>
      </div>
    );
  }),
);
export default Profile;
